import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import FormControlLabel from '@mui/material/FormControlLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Tab from '@mui/material/Tab'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { PERMISSIONS, type InventoryAttachment, type InventoryItemSummary, type InventoryListQuery, type InventoryLot } from '@jupi/shared'
import { ApiError, apiUrl } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { ExpiryChip } from '../components/ExpiryChip'
import { RemoteContent } from '../components/RemoteContent'
import { Title } from '../components/Title'
import { formatDate } from '../format'
import { useSnackbar } from '../hooks/useSnackbar'
import { AttachmentDialog } from './AttachmentDialog'
import { UseStockDialog } from './UseStockDialog'
import { ItemDetailDialog } from './ItemDetailDialog'
import { ItemDialog } from './ItemDialog'
import { LotDialog, draftToLot } from './LotDialog'
import { categoryName, formatValue } from './labels'
import {
  useFleetAircraft,
  useInventoryCategories,
  useInventoryItems,
  useInventoryLocations,
  useInventoryMovements,
  useInventoryTags,
  useInventoryWrites,
} from './queries'

const ALL = '__all__'

// The inventory list: one tab per category over the shared items, with
// search, tag / location / aircraft filters and the expiring and low-stock
// views. Mounted by the admin app.
export function InventoryPage() {
  const { t, i18n } = useTranslation()
  const language = i18n.language
  const { showSnackbar } = useSnackbar()
  const { can } = useAuth()
  const canManage = can(PERMISSIONS.inventoryManage)
  const writes = useInventoryWrites()

  const [tab, setTab] = useState(ALL)
  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')
  const [tag, setTag] = useState('')
  const [locationId, setLocationId] = useState('')
  // /…/inventory?registration=OH-CAM opens already narrowed to that aircraft
  // (the maintenance site's "parts that fit" panel links here).
  const [registration, setRegistration] = useState(() => new URLSearchParams(window.location.search).get('registration')?.toUpperCase() ?? '')
  const [expiring, setExpiring] = useState(false)
  const [lowStock, setLowStock] = useState(false)
  const [archived, setArchived] = useState(false)

  const [openId, setOpenId] = useState<string | null>(null)
  const [itemDialog, setItemDialog] = useState<'new' | 'edit' | null>(null)
  const [lotDialog, setLotDialog] = useState<{ lot: InventoryLot | null } | null>(null)
  const [deletingItem, setDeletingItem] = useState(false)
  const [deletingLot, setDeletingLot] = useState<InventoryLot | null>(null)
  const [attachingLot, setAttachingLot] = useState<InventoryLot | null>(null)
  const [usingLot, setUsingLot] = useState<InventoryLot | null>(null)
  const [deletingFile, setDeletingFile] = useState<InventoryAttachment | null>(null)

  // Search as you type, but ask the server once you pause.
  useEffect(() => {
    const id = setTimeout(() => setQ(search.trim()), 250)
    return () => clearTimeout(id)
  }, [search])

  const filter: Partial<InventoryListQuery> = {
    ...(q ? { q } : {}),
    ...(tab !== ALL ? { category: tab } : {}),
    ...(tag ? { tag } : {}),
    ...(locationId ? { locationId } : {}),
    ...(registration ? { registration } : {}),
    ...(expiring ? { expiring: 'true' as const } : {}),
    ...(lowStock ? { lowStock: 'true' as const } : {}),
    ...(archived ? { archived: 'true' as const } : {}),
  }
  // The export carries the filters on screen.
  const exportQuery = Object.keys(filter).length ? `?${new URLSearchParams(filter as Record<string, string>).toString()}` : ''
  const { data: categories = [] } = useInventoryCategories()
  const { data: locations = [] } = useInventoryLocations()
  const { data: tags = [] } = useInventoryTags()
  const { data: aircraft = [] } = useFleetAircraft()
  const { data: items = [], isLoading, error } = useInventoryItems(filter)
  const { data: allItems = [] } = useInventoryItems({})

  const open = useMemo(() => items.find((i) => i.itemId === openId) ?? allItems.find((i) => i.itemId === openId) ?? null, [items, allItems, openId])
  const { data: movements = [] } = useInventoryMovements(openId)
  const lowCount = allItems.filter((i) => i.lowStock).length
  const expiringCount = allItems.filter((i) => i.expiring).length
  const categoryOf = (key: string) => categories.find((c) => c.categoryKey === key)

  const run = async (op: () => Promise<unknown>, done?: () => void) => {
    try {
      await op()
      showSnackbar(t('inventory.saved'))
      done?.()
    } catch (err) {
      showSnackbar(err instanceof ApiError ? err.message : t('common.error'), { severity: 'error' })
    }
  }

  const defaultCategory = tab !== ALL ? tab : (categories[0]?.categoryKey ?? '')
  const lotLocations = (item: InventoryItemSummary) => [...new Set(item.lots.filter((l) => l.quantity > 0 && l.locationName).map((l) => l.locationName as string))]

  return (
    <Box>
      <Title label={t('inventory.title')}>
        <Stack direction="row" spacing={1}>
          <Button component="a" href={apiUrl(`/api/inventory/export${exportQuery}`)}>
            {t('inventory.exportCsv')}
          </Button>
          {canManage ? (
            <Button variant="contained" onClick={() => setItemDialog('new')} disabled={categories.length === 0}>
              {t('inventory.add')}
            </Button>
          ) : null}
        </Stack>
      </Title>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('inventory.hint')}
      </Typography>

      <Tabs value={tab} onChange={(_, v: string) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2 }}>
        <Tab value={ALL} label={t('inventory.all')} />
        {categories.map((c) => (
          <Tab key={c.categoryKey} value={c.categoryKey} label={categoryName(c, language)} />
        ))}
      </Tabs>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField size="small" label={t('inventory.search')} value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 2 }} />
        <Autocomplete
          size="small"
          sx={{ flex: 1, minWidth: 140 }}
          options={tags.map((x) => x.tag)}
          value={tag || null}
          onChange={(_, value) => setTag(value ?? '')}
          renderInput={(params) => <TextField {...params} label={t('inventory.tagFilter')} />}
        />
        <TextField select size="small" label={t('inventory.location')} value={locationId} onChange={(e) => setLocationId(e.target.value)} sx={{ flex: 1, minWidth: 140 }}>
          <MenuItem value="">{t('inventory.allLocations')}</MenuItem>
          {locations.map((l) => (
            <MenuItem key={l.locationId} value={l.locationId}>
              {l.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label={t('inventory.aircraftFilter')} value={registration} onChange={(e) => setRegistration(e.target.value)} sx={{ flex: 1, minWidth: 140 }}>
          <MenuItem value="">{t('inventory.anyAircraft')}</MenuItem>
          {aircraft.map((a) => (
            <MenuItem key={a.registration} value={a.registration}>
              {a.registration}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <Stack direction="row" spacing={1} useFlexGap sx={{ mb: 1, flexWrap: 'wrap' }}>
        {lowCount > 0 ? (
          <Chip color="warning" clickable variant={lowStock ? 'filled' : 'outlined'} label={t('inventory.lowCount', { count: lowCount })} onClick={() => setLowStock((v) => !v)} />
        ) : null}
        {expiringCount > 0 ? (
          <Chip color="warning" clickable variant={expiring ? 'filled' : 'outlined'} label={t('inventory.expiringCount', { count: expiringCount })} onClick={() => setExpiring((v) => !v)} />
        ) : null}
      </Stack>
      <Stack direction="row" spacing={2} useFlexGap sx={{ mb: 2, flexWrap: 'wrap' }}>
        <FormControlLabel control={<Switch checked={expiring} onChange={(e) => setExpiring(e.target.checked)} />} label={t('inventory.expiring')} />
        <FormControlLabel control={<Switch checked={lowStock} onChange={(e) => setLowStock(e.target.checked)} />} label={t('inventory.lowStock')} />
        <FormControlLabel control={<Switch checked={archived} onChange={(e) => setArchived(e.target.checked)} />} label={t('inventory.showArchived')} />
      </Stack>

      <RemoteContent isLoading={isLoading} error={error}>
        {items.length === 0 ? (
          <Typography color="text.secondary">{t('inventory.none')}</Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('inventory.name')}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{t('inventory.fits')}</TableCell>
                  <TableCell>{t('inventory.inStock')}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{t('inventory.location')}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{t('inventory.nextExpiry')}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{t('inventory.value')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.itemId} hover onClick={() => setOpenId(item.itemId)} sx={{ cursor: 'pointer', opacity: item.archivedAt ? 0.6 : 1 }}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600 }}>{item.name}</Typography>
                      {item.partNumber ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {item.partNumber}
                          {item.manufacturer ? ` · ${item.manufacturer}` : ''}
                        </Typography>
                      ) : null}
                      {item.tags.length ? (
                        <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap', mt: 0.5 }}>
                          {item.tags.map((x) => (
                            <Chip key={x} size="small" label={x} onClick={(e) => { e.stopPropagation(); setTag(x) }} />
                          ))}
                        </Stack>
                      ) : null}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {item.fitments.length ? item.fitments.map((f) => f.registration ?? f.aircraftType).join(', ') : '—'}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {item.usableQuantity} {item.unit}{' '}
                      {item.lowStock ? <Chip size="small" color="warning" label={t('inventory.lowBadge')} /> : null}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{lotLocations(item).join(', ') || '—'}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, whiteSpace: 'nowrap' }}>
                      {item.nextExpiry ? (
                        <>
                          {formatDate(item.nextExpiry)} <ExpiryChip expiryDate={item.nextExpiry} />
                        </>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, whiteSpace: 'nowrap' }}>
                      {Object.keys(item.valueByCurrency).length ? formatValue(item.valueByCurrency, language) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </RemoteContent>

      <ItemDetailDialog
        item={open}
        category={open ? categoryOf(open.categoryKey) : undefined}
        canManage={canManage}
        language={language}
        onClose={() => setOpenId(null)}
        onEdit={() => setItemDialog('edit')}
        onArchive={() => open && run(() => writes.updateItem.mutateAsync({ itemId: open.itemId, archived: !open.archivedAt }))}
        onDelete={() => setDeletingItem(true)}
        onAddLot={() => setLotDialog({ lot: null })}
        onEditLot={(lot) => setLotDialog({ lot })}
        onDeleteLot={setDeletingLot}
        onAttach={setAttachingLot}
        onDeleteAttachment={setDeletingFile}
        onUse={setUsingLot}
        movements={movements}
      />

      <ItemDialog
        open={itemDialog !== null}
        item={itemDialog === 'edit' ? open : null}
        defaultCategoryKey={defaultCategory}
        categories={categories}
        locations={locations}
        aircraft={aircraft}
        knownTags={tags.map((x) => x.tag)}
        otherItems={allItems}
        saving={writes.createItem.isPending || writes.updateItem.isPending}
        language={language}
        onClose={() => setItemDialog(null)}
        onSave={(body) => {
          const editing = itemDialog === 'edit' && open
          void run(
            async () => {
              if (editing) {
                const { lot: _lot, ...changes } = body
                await writes.updateItem.mutateAsync({ itemId: open.itemId, ...changes, fitments: body.fitments })
              } else {
                const created = await writes.createItem.mutateAsync(body)
                setOpenId(created.item.itemId)
              }
            },
            () => setItemDialog(null),
          )
        }}
      />

      <LotDialog
        open={lotDialog !== null}
        lot={lotDialog?.lot ?? null}
        locations={locations}
        showSerial={open ? (categoryOf(open.categoryKey)?.aircraftRelated ?? false) : false}
        saving={writes.createLot.isPending || writes.updateLot.isPending}
        onClose={() => setLotDialog(null)}
        onSave={(draft) => {
          const existing = lotDialog?.lot
          if (!open) return
          void run(
            () =>
              existing
                ? writes.updateLot.mutateAsync({ lotId: existing.lotId, ...draftToLot(draft) })
                : writes.createLot.mutateAsync({ itemId: open.itemId, ...draftToLot(draft) }),
            () => setLotDialog(null),
          )
        }}
      />

      <UseStockDialog
        open={usingLot !== null}
        max={usingLot?.quantity ?? 0}
        unit={open?.unit ?? ''}
        aircraft={aircraft}
        saving={writes.useStock.isPending}
        onClose={() => setUsingLot(null)}
        onUse={(use) => {
          const lot = usingLot
          if (lot) void run(() => writes.useStock.mutateAsync({ lotId: lot.lotId, ...use }), () => setUsingLot(null))
        }}
      />
      <AttachmentDialog
        open={attachingLot !== null}
        saving={writes.uploadAttachment.isPending}
        onClose={() => setAttachingLot(null)}
        onUpload={(kind, file) => {
          const lot = attachingLot
          if (lot) void run(() => writes.uploadAttachment.mutateAsync({ lotId: lot.lotId, kind, file }), () => setAttachingLot(null))
        }}
      />
      <ConfirmDialog
        open={deletingFile !== null}
        title={t('inventory.deleteFileTitle')}
        message={t('inventory.deleteFileMessage', { name: deletingFile?.filename })}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        onClose={() => setDeletingFile(null)}
        onConfirm={() => {
          const file = deletingFile
          setDeletingFile(null)
          if (file) void run(() => writes.deleteAttachment.mutateAsync(file.attachmentId))
        }}
      />
      <ConfirmDialog
        open={deletingItem}
        title={t('inventory.deleteItemTitle')}
        message={t('inventory.deleteItemMessage', { name: open?.name })}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        onClose={() => setDeletingItem(false)}
        onConfirm={() => {
          const id = open?.itemId
          setDeletingItem(false)
          if (id) void run(() => writes.deleteItem.mutateAsync(id), () => setOpenId(null))
        }}
      />
      <ConfirmDialog
        open={deletingLot !== null}
        title={t('inventory.deleteLotTitle')}
        message={t('inventory.deleteLotMessage')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        onClose={() => setDeletingLot(null)}
        onConfirm={() => {
          const lot = deletingLot
          setDeletingLot(null)
          if (lot) void run(() => writes.deleteLot.mutateAsync(lot.lotId))
        }}
      />
    </Box>
  )
}
